import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard,
  FileText,
  Clock,
  Receipt,
  Users,
  Settings,
  LogOut,
  Search,
  FileCheck,
  FolderOpen,
  Zap,
  Shield,
  FileSignature,
  ClipboardList,
} from 'lucide-react';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import NotificationCenter from './NotificationCenter';

const DashboardLayout = () => {
  const { user, logout, hasModule } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isTenantAdmin = user?.role === 'tenant_admin' || user?.role === 'super_admin';

  const hasAnyModule = (moduleKeys = []) => moduleKeys.some((key) => hasModule(key));

  // Core items always visible, except Documents which is gated by document-related modules
  const coreItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    {
      to: '/documents',
      icon: FileText,
      label: 'Documents',
      moduleKeys: ['document_submission', 'timesheet_ocr', 'accounting_intake', 'accounting_repository'],
    },
    { to: '/review-queue', icon: FileCheck, label: 'Review Queue' },
    { to: '/jobs', icon: Zap, label: 'Jobs' },
    { to: '/search', icon: Search, label: 'Search' },
  ].filter((item) => !item.moduleKeys || hasAnyModule(item.moduleKeys));

  // Module-gated items
  const moduleItems = [
    { to: '/timesheet', icon: Clock, label: 'Timesheets', module: 'timesheet_ocr' },
    { to: '/intake', icon: Receipt, label: 'Client Intake', module: 'accounting_intake' },
    { to: '/contracts', icon: FileSignature, label: 'Contracts', module: 'contracts' },
  ].filter((item) => hasModule(item.module));

  // Admin items (tenant admin only)
  const adminItems = [
    ...(isTenantAdmin ? [{ to: '/admin/users', icon: Users, label: 'Users' }] : []),
    ...(isTenantAdmin && hasModule('document_submission')
      ? [{ to: '/admin/document-settings', icon: ClipboardList, label: 'Document Settings' }]
      : []),
    ...(isTenantAdmin ? [{ to: '/audit-logs', icon: Shield, label: 'Audit Logs' }] : []),
  ];

  const renderNav = (items, label) => (
    <>
      {label && items.length > 0 && (
        <p className="px-3 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</p>
      )}
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
              isActive ? 'bg-indigo-50 text-indigo-600' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`
          }
          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <item.icon className="h-5 w-5" />
          {item.label}
        </NavLink>
      ))}
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col shrink-0">
        <div className="p-5 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-indigo-600 flex items-center justify-center text-white text-sm font-bold">C</div>
            <div>
              <h1 className="text-base font-semibold text-slate-900">CoreDocs</h1>
              <p className="text-[11px] text-slate-400">Tenant Workspace</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {renderNav(coreItems)}
          {renderNav(moduleItems, 'Modules')}
          {renderNav(adminItems, 'Administration')}
        </nav>

        {/* User footer */}
        <div className="p-4 border-t border-slate-100">
          <div className="flex items-center gap-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-indigo-100 text-indigo-600 text-xs">
                {(user?.first_name?.[0] || '') + (user?.last_name?.[0] || '')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-400 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
            </div>
            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-slate-600" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-end px-6 shrink-0">
          <div className="flex items-center gap-4">
            <NotificationCenter />
          </div>
        </header>

        {/* Content area */}
        <main className="flex-1 overflow-y-auto p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
